import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import { AccessCode2026 } from '@/components/standard2026/Standard2026';
import { EntryAccessPanel } from '@/components/surveyAccess/EntryAccessPanel';
import { paramsFromScan } from '@/components/surveyAccess/QrScanButton';

const NETWORK_MSG = "We couldn't check your code. Please check your connection and try again.";
const NOT_RECEIPT_QR = "That QR code isn't a receipt survey code. Please scan the QR code printed on your receipt.";

// Phase 4B — stable browser session id for anti-guessing (no personal data).
const accessSessionId = () => {
  try {
    let id = localStorage.getItem('sf_access_sid');
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, '').slice(0, 40);
      localStorage.setItem('sf_access_sid', id);
    }
    return id;
  } catch { return undefined; }
};

const postValidate = async (url, body) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { valid: false, message: data.detail || NETWORK_MSG };
  return data;
};

// Phase 3 access gate: QR Direct Access (URL params, auto-checked) and/or a
// Manual Numeric Code. Both resolve to one credential passed to the submit.
// With `entry` (generic public URL) the same shared validator runs without a link
// branch and returns the branch the credential maps to (onGranted's 2nd arg).
export const SurveyAccessGate = ({ access, branchId, code, entry, brandName, isStd2026, primary, accessQuestion, header, progress, footer, onShown, onGranted }) => {
  const qrParams = useMemo(() => Object.fromEntries(new URLSearchParams(window.location.search)), []);
  const sessionId = useMemo(accessSessionId, []);
  const qrOn = !!access.methods?.qr;
  const manualOn = !!access.methods?.manual;
  const hasQrParams = qrOn && Object.keys(qrParams).length > 0;
  const autoCheck = hasQrParams || (!entry && !manualOn);
  const [checking, setChecking] = useState(!access.config_error && autoCheck);
  const [error, setError] = useState(access.config_error ? "Feedback codes can't be checked right now. Please try again later." : '');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const validate = (body) => (entry
    ? postValidate(`${API}/public/entry/validate`, { ...entry.key, ...body, session_id: sessionId })
    : postValidate(`${API}/public/survey/access/validate`, { code, branch_id: branchId, ...body, session_id: sessionId }));

  useEffect(() => {
    if (access.config_error || !autoCheck) return;
    if (!hasQrParams && !access.required) { onGranted(null); return; }
    validate({ method: 'qr', qr_params: qrParams })
      .then((r) => {
        if (r.valid) { onGranted({ method: 'qr', qr_params: qrParams, session_id: sessionId }, r); return; }
        // Unrelated URL params only (e.g. tracking) — fall back to manual entry silently.
        if (!(r.status === 'missing' && (manualOn || entry))) setError(r.message);
        if (entry) window.history.replaceState(null, '', window.location.pathname);
        setChecking(false);
      })
      .catch(() => { setError(NETWORK_MSG); setChecking(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The screen is "seen" only once it renders something other than the QR check.
  useEffect(() => { if (!checking) onShown?.(); }, [checking]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (body, credential) => {
    setBusy(true);
    setError('');
    try {
      const r = await validate(body);
      if (r.valid) onGranted({ ...credential, session_id: sessionId }, r);
      else setError(r.status === 'missing' && body.method === 'qr' ? NOT_RECEIPT_QR : r.message);
    } catch { setError(NETWORK_MSG); }
    setBusy(false);
  };
  const submitManual = () => run({ method: 'manual', access_code: value }, { method: 'manual', access_code: value });
  const onScanned = (text) => {
    const params = paramsFromScan(text);
    if (!Object.keys(params).length) { setError(NOT_RECEIPT_QR); return; }
    run({ method: 'qr', qr_params: params }, { method: 'qr', qr_params: params });
  };

  const q = { id: 'gate', access_label: 'Feedback code', access_placeholder: 'Enter the code on your receipt', ...(accessQuestion || {}) };
  const errorBox = error && (
    <p role="alert" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEF2F2] rounded-lg px-3 py-2" data-testid="access-error">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
    </p>
  );
  return (
    <div className={`min-h-screen bg-[#F0F2F5] flex flex-col${isStd2026 ? ' std2026' : ''}`} data-testid="survey-access-gate">
      {header}
      {!checking && progress}
      <main className="flex-1 px-4 py-6">
        <div className={`max-w-md md:max-w-2xl mx-auto bg-white rounded-2xl p-5 md:p-7 shadow-sm space-y-4${isStd2026 ? ' std2026-container' : ''}`}>
          {checking ? (
            <p className="flex items-center gap-2 text-sm text-[#64748B]" data-testid="access-checking">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking your QR link…
            </p>
          ) : entry ? (
            <>
              {errorBox}
              {!access.config_error && (
                <EntryAccessPanel settings={entry.settings} brandName={brandName} qrOn={qrOn} manualOn={manualOn}
                  value={value} onValue={setValue} onContinue={submitManual} onScanned={onScanned} busy={busy} primary={primary} />
              )}
            </>
          ) : (
            <>
              {manualOn && !access.config_error && (
                <>
                  <h2 className={isStd2026 ? 'std2026-qtitle' : 'text-lg font-semibold text-[#0F172A]'} data-testid="access-title">
                    {accessQuestion?.text || 'Enter your feedback code'}
                  </h2>
                  <div className={isStd2026 ? '' : 'std2026'}>
                    <AccessCode2026 question={q} answer={value} onChange={setValue} primaryColor={primary} showQrHint={qrOn} />
                  </div>
                </>
              )}
              {errorBox}
              {manualOn && !access.config_error && (
                <Button onClick={submitManual} disabled={busy || !value.trim()} className="w-full text-white" style={{ backgroundColor: primary }} data-testid="access-continue-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
                </Button>
              )}
              {!access.required && !access.config_error && manualOn && (
                <button type="button" onClick={() => onGranted(null)} className="w-full text-xs text-[#64748B] underline" data-testid="access-skip-btn">
                  Continue without a code
                </button>
              )}
            </>
          )}
        </div>
      </main>
      {footer}
    </div>
  );
};
